import { computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import { useExperiments } from '@composables/useExperiments';

import { useFeatureEligibility } from '@modules/featureControl';
import { GET_PRODUCT_IMAGES_CTA_SORTED_LIST } from '../../types/getters';
import { PRODUCT_IMAGES_CTA_SET_ITEMS_ORDER } from '../../types/actions';
import {
  UPDATE_PRODUCT_IMAGES_CTA_UPDATE_DIMENSIONS_ENTRY,
  UPDATE_PRODUCT_IMAGES_CTA_UPDATE_ITEMS_HEIGHT,
  UPDATE_PRODUCT_IMAGES_CTA_UPDATE_ITEMS_WIDTH,
} from '../../types/mutations';

/**
 * Import each CTA item composable which have their own module specific
 * logic needed to be triggered during page load
 */
import { useVtoCta } from './useVtoCta';
import { useWebAr } from './useWebAr';
import { usePartOfCollection } from './usePartOfCollection';
import { useOutfittingCta } from './useOutfittingCta';

/**
 *
 * @param {string} [productId = '']
 * @param {import('vue').ComputedRef<html>} [productCtaContainer = null]
 * @return {Object}
 */
export function useProductImagesCta(productId = '', productCtaContainer = null) {
  const store = useStore();

  const { experimentFlags } = useExperiments();
  const shouldShowOutfittingCta = !experimentFlags.shopTheLookEnabled;
  const { isShoppable } = useFeatureEligibility();

  const { init: outfittingCtaInit } = useOutfittingCta();
  const { init: partOfCollectionCtaInit } = usePartOfCollection();
  const { init: useVtoCtaInit } = useVtoCta();

  const sortedList = computed(() => store.getters[`productImagesCtaStore/${GET_PRODUCT_IMAGES_CTA_SORTED_LIST}`]);

  /**
   * Set CTA items order
   * @param {string[]} ids
   */
  const setItemsOrder = async (ids) => {
    // Filter ids leaving out any non string
    const filteredIds = ids.filter((item) => typeof item === 'string');
    await store.dispatch(`productImagesCtaStore/${PRODUCT_IMAGES_CTA_SET_ITEMS_ORDER}`, filteredIds);
  };

  const pdpEntriesInit = () => (
    [setItemsOrder(['outfitting']),
      shouldShowOutfittingCta ? outfittingCtaInit() : Promise.resolve(),
    ]
  );

  const init = () => Promise.all([
    setItemsOrder([
      'partOfCollection',
      'hexa',
      'marxent',
      'vto',
      'outfitting',
    ]),
    useVtoCtaInit(),
    shouldShowOutfittingCta ? outfittingCtaInit() : Promise.resolve(),
    partOfCollectionCtaInit(),
    useWebAr(sortedList),
  ]);

  onMounted(isShoppable ? pdpEntriesInit : init);

  if (productId && productCtaContainer) {
    // create new cta dimensions entry
    store.commit(`productImagesCtaStore/${UPDATE_PRODUCT_IMAGES_CTA_UPDATE_DIMENSIONS_ENTRY}`, productId);

    // get ct container dimensions
    const { height, width } = useElementSize(productCtaContainer);

    watch([
      () => height.value,
      () => width.value,
    ], ([newHeight, newWidth]) => {
      if (newHeight) {
        store.commit(`productImagesCtaStore/${UPDATE_PRODUCT_IMAGES_CTA_UPDATE_ITEMS_HEIGHT}`, { key: productId, height: newHeight });
      }
      if (newWidth) {
        store.commit(`productImagesCtaStore/${UPDATE_PRODUCT_IMAGES_CTA_UPDATE_ITEMS_WIDTH}`, { key: productId, width: newWidth });
      }
    }, { immediate: true });
  }

  return {
    init,
    sortedList,
  };
}

export default useProductImagesCta;
